import React, { useState, useEffect, useRef } from 'react';

// interfaces
import { ISelectButtonList } from '../../../interfaces/lists/ISelectButtonList';



const SelectButtonList = (
    {
        buttons = [], 
        className = '', 
        allButtonOnClick, 
        fixedPosition 
    }: ISelectButtonList) => {


    const [focusedIndex, setFocusedIndex] = useState<number | null>(0); 
    const listRef = useRef<HTMLUListElement>(null);
    
    const handleKeyDown = (event: React.KeyboardEvent<HTMLUListElement>) => {
        if (focusedIndex === null) return;

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                setFocusedIndex((prev) => (prev === null ? 0 : Math.min(prev + 1, buttons.length - 1)));
                break;
            case 'ArrowUp':
                event.preventDefault();
                setFocusedIndex((prev) => (prev === null ? 0 : Math.max(prev - 1, 0)));
                break;
            case 'Enter':
            case ' ':
                event.preventDefault();
                handleClick(buttons[focusedIndex].onClick);
                break;
            case 'Escape':
                setFocusedIndex(null);
                break;
            default:
                break;
        }
    };

    useEffect(() => {
        if (focusedIndex !== null && listRef.current) {
            const focusedItem = listRef.current.children[focusedIndex] as HTMLLIElement;
            if (focusedItem) {
                focusedItem?.scrollIntoView?.({ block: 'nearest' });
                const buttonElement = focusedItem.querySelector('button');
                if (buttonElement) {
                    buttonElement.focus(); // Focus the button itself
                }
            }
        }
    }, [focusedIndex]);

    // Conditionally render the list only if there are buttons
    if (!buttons || buttons.length === 0) {
        return null;
    }

    function handleClick(buttonOnClick: () => void) {
        buttonOnClick();
        if (allButtonOnClick) {
            allButtonOnClick();
        }
    }

    return (
        <ul 
            ref={listRef} 
            className={`select-button-list ${buttons.length === 1 ? 'single-button' : ''} ${className} ${fixedPosition ? 'fixed-position' : ''}`} 
            role="listbox"  
            aria-activedescendant={focusedIndex !== null ? `button-${focusedIndex}` : undefined} 
            tabIndex={0} 
            onKeyDown={handleKeyDown} 
            style={fixedPosition || undefined}
        >
            {buttons.map((button, index) => (
                button && (
                    <li 
                        key={index} 
                        id={`button-${index}`} 
                        className="select-button-list-item" 
                        role="option" 
                        aria-selected={focusedIndex === index} 
                    >
                        <button 
                            type='button' 
                            className={`select-button ${button.isSelected && `selected-button`}`}  
                            onClick={(e) => handleClick(() => button.onClick(e))} 
                            disabled={button?.isDisabled}
                        >
                            {button.content}
                        </button>
                    </li>
                )
            ))}
        </ul>
    );
};

export default SelectButtonList;
